import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Order, OrderService, OrderStatus, Product, ProductService } from "../api/api_pb";
import { useClient } from "../connect_api";
import { toast } from "react-toastify";
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import CurrencyFormatter from "../utils/currencyFormatter";
import { convertTimestampToDateWithTimezone } from "../utils/dateUtils";
import RelativeTime from "../components/RelativeTime";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Papa from "papaparse";
import moment from 'moment-timezone';
import { useAuth } from "../AuthContext";
import {
    FileText,
    Download,
    Calendar,
    DollarSign,
    Package,
    CreditCard,
    User,
    Clock,
    RotateCcw,
    CheckCircle,
    XCircle,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    ShoppingCart,
    Truck,
    Shield,
    Globe
} from 'lucide-react';
import { AuthMethod } from "../common/v1/types_pb";
import OrderFilters, { OrderFiltersType } from '../components/OrderFilters';
import MobileNumberActions from '../components/MobileNumberActions';
import SendSMSModal from '../components/SendSMSModal';
import NayaxCaptureActions from '../components/NayaxCaptureActions';
import { isRefundable } from '../utils/nayaxUtils';

const Leases: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [itemsPerPage] = useState<number>(12);
    const [totalOrders, setTotalOrders] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isRefunding, setIsRefunding] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const orderClient = useClient(OrderService);
    const productClient = useClient(ProductService);
    const [refundNote, setRefundNote] = useState<string>("");
    const [products, setProducts] = useState<Product[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list'); // Default to list view
    const [smsTarget, setSmsTarget] = useState<{ uuid: string; mobileNumber: string } | null>(null);
    const [filters, setFilters] = useState<OrderFiltersType>({
        machineUuids: [],
        last4Digits: '',
        productUuids: []
    });
    const { organizationInfo } = useAuth();

    const handleStartDateChange = (date: Date | null) => {
        console.log('date', date);
        navigate(`?page=1`);
        setStartDate(date);
    };

    const handleEndDateChange = (date: Date | null) => {
        navigate(`?page=1`);
        setEndDate(date);
    };

    const handleFiltersChange = useCallback((newFilters: OrderFiltersType) => {
        setFilters(newFilters);
        navigate(`?page=1`);
    }, [navigate]);

    const handleApplyFilters = useCallback(() => {
        navigate(`?page=1`);
        // fetchOrders will be called by useEffect when page changes
    }, [navigate]);

    const handleClearFilters = useCallback(() => {
        setFilters({
            machineUuids: [],
            last4Digits: '',
            productUuids: []
        });
        navigate(`?page=1`);
    }, [navigate]);

    const getTimestamp = (date: Date | null, timezone: string, isEnd: boolean = false): { seconds: bigint, nanos: number } | null => {
        if (!date) return null;
        if (!timezone) {
            console.error('Timezone is undefined or empty');
            return null;
        }

        const momentDate = moment(date).tz(timezone);

        if (isEnd) {
            momentDate.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
        } else {
            momentDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        }
        const timestamp = momentDate.unix();
        console.log(`Converted timestamp for ${isEnd ? 'end' : 'start'} time:`, timestamp);

        return { seconds: BigInt(timestamp), nanos: 0 };
    };

    const fetchProducts = useCallback(async () => {
        try {
            const response = await productClient.listProducts({});
            setProducts(response.products);
        } catch (err) {
            console.error('Failed to fetch products:', err);
        }
    }, [productClient]);

    const downloadCsv = async () => {
        setIsLoading(true);
        setError(null);

        const startTimestamp = getTimestamp(startDate, organizationInfo?.timezone ?? '', false);
        const endTimestamp = getTimestamp(endDate, organizationInfo?.timezone ?? '', true);

        try {
            const response = await orderClient.listOrders({
                limit: 100000,
                page: 0, // API uses 0-based pagination
                startTime: startTimestamp || undefined,
                finishTime: endTimestamp || undefined,
                machineUuids: filters.machineUuids,
                last4Digits: filters.last4Digits,
                productUuids: filters.productUuids,
            });

            const excludedStatuses = new Set([
                "UNSPECIFIED",
                "PENDING_PAYMENT",
                "PENDING_PAYMENT_TIMEOUT",
                "CANCELED"
            ]);

            const CurrencyFormatterWith = (priceInCents: number, currencyDecimalPoint: number) => {
                const divisor = Math.pow(10, currencyDecimalPoint);
                const roundedValue = Number((priceInCents / divisor).toFixed(currencyDecimalPoint));

                return `${roundedValue.toLocaleString(undefined, {
                    minimumFractionDigits: currencyDecimalPoint,
                    maximumFractionDigits: currencyDecimalPoint,
                })}`;
            };


            const csvData = response.orders
                .filter(order => !excludedStatuses.has(OrderStatus[order.status]))
                .map(order => ({
                    "Created At": order.createdAt ? convertTimestampToDateWithTimezone(order.createdAt, organizationInfo?.timezone ?? '') : '',
                    "Order UUID": order.uuid,
                    "Status": OrderStatus[order.status],
                    "Auth Method": getAuthMethodDisplay(order.authMethod),
                    "Card Brand": order.paymentInformation?.cardBrand || '',
                    "Last4": order.paymentInformation?.last4 || '',
                    "Customer": order.customer?.mobileNumber || '',
                    "Product": order.productName,
                    "Price": order.priceInCents ? CurrencyFormatterWith(order.priceInCents, organizationInfo?.currencyDecimalPoint ?? 0) : '',
                    "Machine": order.machineName,
                    "Door": order.doorIdDisplay,
                    "Trackable Item": order.trackableItemName,
                    "Dispensed At": order.dispensedAt ? convertTimestampToDateWithTimezone(order.dispensedAt, organizationInfo?.timezone ?? '') : '',
                    "Returned At": order.returnedAt ? convertTimestampToDateWithTimezone(order.returnedAt, organizationInfo?.timezone ?? '') : '',
                    "Virtual Product": order.isVirtualProduct ? "true" : "false",
                    "Virtual SKU": order.virtualProductSku || '',
                    "Virtual Product Name": order.virtualProductName || '',
                    "Virtual Category": order.virtualCategoryName || '',
                    "Confirmation ID": order.virtualConfirmationId || '',
                    "Refund": order.refundStatus?.refunded ? "true" : "false",
                    "Refunded At": order.refundStatus?.refundedAt ? convertTimestampToDateWithTimezone(order.refundStatus.refundedAt, organizationInfo?.timezone ?? '') : '',
                    "Created At (UTC)": order.createdAt ? convertTimestampToDateWithTimezone(order.createdAt, 'UTC') : '',
                    "Dispensed At (UTC)": order.dispensedAt ? convertTimestampToDateWithTimezone(order.dispensedAt, 'UTC') : '',
                    "Returned At (UTC)": order.returnedAt ? convertTimestampToDateWithTimezone(order.returnedAt, 'UTC') : '',
                    "Refunded At (UTC)": order.refundStatus?.refundedAt ? convertTimestampToDateWithTimezone(order.refundStatus.refundedAt, 'UTC') : '',
                }));

            const csv = Papa.unparse(csvData);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `order_list_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError('Failed to load data. Please try again later.');
            toast.dismiss();
            toast.error('Failed to download csv');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const query = new URLSearchParams(location.search);
    const pageParam = query.get('page');
    const page = pageParam ? Math.abs(parseInt(pageParam, 10)) : 1;

    const fetchOrdersByDate = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        const startTimestamp = getTimestamp(startDate, organizationInfo?.timezone ?? '', false);
        const endTimestamp = getTimestamp(endDate, organizationInfo?.timezone ?? '', true);

        try {
            const response = await orderClient.listOrders({
                limit: itemsPerPage,
                page: page - 1, // API uses 0-based pagination, UI uses 1-based
                startTime: startTimestamp || undefined,
                finishTime: endTimestamp || undefined,
                machineUuids: filters.machineUuids,
                last4Digits: filters.last4Digits,
                productUuids: filters.productUuids,
            });
            setOrders(response.orders);
            setTotalOrders(response.total);
        } catch (err) {
            setError('Failed to load data. Please try again later.');
            toast.dismiss();
            toast.error('Failed to fetch data');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, organizationInfo, orderClient, itemsPerPage, page, filters]);

    const fetchOrders = useCallback(async () => {
        fetchOrdersByDate();
    }, [fetchOrdersByDate]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        fetchOrders();
        navigate(`?page=${page}`);
    }, [fetchOrders, page, navigate]);


    const totalPages = Math.ceil(totalOrders / itemsPerPage);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            navigate(`?page=${newPage}`);
        }
    };

    const handleRefundClick = (order: Order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const confirmRefund = () => {
        if (!selectedOrder || isRefunding) return;
        setIsRefunding(true);

        orderClient.refund({
            orderUuid: selectedOrder.uuid,
            refundNote: refundNote,
        }).then(() => {
            toast.dismiss();
            toast.success("The order has been successfully refunded");
            fetchOrders();
        }).catch((err: any) => {
            toast.dismiss();
            toast.error('Failed to refund order');
            console.log('error', err);
        }).finally(() => {
            setIsRefunding(false);
            setSelectedOrder(null);
            setShowModal(false);
        });
    };


    const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRefundNote(e.target.value);
    };

    const handleRefundModalClose = () => {
        setShowModal(false);
        setRefundNote("");
        setSelectedOrder(null);
    };

    const getStatusColor = (status: number) => {
        const statusName = OrderStatus[status];
        switch (statusName) {
            case 'COMPLETED':
            case 'VIRTUAL_FULFILLED':
                return 'bg-green-100 text-green-800';
            case 'DISPENSED':
                return 'bg-blue-100 text-blue-800';
            case 'PENDING_PAYMENT':
                return 'bg-yellow-100 text-yellow-800';
            case 'CANCELED':
            case 'PENDING_PAYMENT_TIMEOUT':
            case 'VIRTUAL_FULFILLMENT_FAILURE':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: number) => {
        const statusName = OrderStatus[status];
        switch (statusName) {
            case 'COMPLETED':
                return <CheckCircle className="w-4 h-4" />;
            case 'VIRTUAL_FULFILLED':
                return <Globe className="w-4 h-4" />;
            case 'DISPENSED':
                return <Truck className="w-4 h-4" />;
            case 'PENDING_PAYMENT':
                return <Clock className="w-4 h-4" />;
            case 'CANCELED':
            case 'PENDING_PAYMENT_TIMEOUT':
            case 'VIRTUAL_FULFILLMENT_FAILURE':
                return <XCircle className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    const getAuthMethodDisplay = (authMethod: AuthMethod) => {
        switch (authMethod) {
            case AuthMethod.MOBILE_PIN:
                return 'Mobile PIN';
            case AuthMethod.VENUE_ACCESS_CODE:
                return 'Venue Code';
            case AuthMethod.MOBILE_EXPRESS:
                return 'Express';
            default:
                return 'Unknown';
        }
    };

    const isProductForSale = (productUuid: string): boolean => {
        const product = products.find(p => p.uuid === productUuid);
        return product?.forSale ?? false;
    };

    const OrderCard = ({ order }: { order: Order }) => (
        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Order ID</p>
                        <p className="font-mono text-sm text-gray-900 break-all">{order.uuid}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {order.isVirtualProduct && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <Globe className="w-3 h-3" />
                                Virtual
                            </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {OrderStatus[order.status]}
                        </span>
                    </div>
                </div>

                {/* Customer & Product Info */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2">
                        <User className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Customer</span>
                            {order.customer ? (
                                <MobileNumberActions
                                    mobileNumber={order.customer.mobileNumber}
                                    customerUuid={order.customer.uuid}
                                    onSMSClick={() => setSmsTarget({ uuid: order.customer!.uuid, mobileNumber: order.customer!.mobileNumber })}
                                />
                            ) : (
                                <span className="text-gray-500">Unknown</span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-start gap-2">
                        <ShoppingCart className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Product</span>
                            <div className="flex items-center gap-2">
                                <p className="text-gray-900 font-medium">
                                    {order.isVirtualProduct ? order.virtualProductName || order.productName : order.productName}
                                </p>
                                {order.isVirtualProduct && order.virtualCategoryName && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                        {order.virtualCategoryName}
                                    </span>
                                )}
                                {isProductForSale(order.productUuid) && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        For Sale
                                    </span>
                                )}
                            </div>
                            {order.isVirtualProduct && order.virtualProductSku && (
                                <p className="text-xs text-gray-500 mt-0.5">SKU: {order.virtualProductSku}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Price</span>
                            <p className="text-gray-900 font-bold text-lg">
                                <CurrencyFormatter priceInCents={order.priceInCents} />
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-2">
                        <Package className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Machine / Location</span>
                            <p className="text-gray-900">{order.machineName}</p>
                            <p className="text-sm text-gray-500">Door: {order.doorIdDisplay}</p>
                        </div>
                    </div>

                    {order.paymentInformation && (
                        <div className="flex items-start gap-2">
                            <CreditCard className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-sm text-gray-600">Payment</span>
                                <p className="text-gray-900">{order.paymentInformation.cardBrand} •••• {order.paymentInformation.last4}</p>
                                {order.isNayaxOrder && (order.paymentInformation.rrn || order.paymentInformation.paymentDeviceSessionId) && (
                                    <p className="text-xs text-gray-500">
                                        {order.paymentInformation.rrn && <>RRN: {order.paymentInformation.rrn}</>}
                                        {order.paymentInformation.rrn && order.paymentInformation.paymentDeviceSessionId && <> · </>}
                                        {order.paymentInformation.paymentDeviceSessionId && <>Session: {order.paymentInformation.paymentDeviceSessionId}</>}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {order.trackableItemName && (
                        <div className="flex items-start gap-2">
                            <Package className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-sm text-gray-600">Trackable Item</span>
                                <p className="text-gray-900">{order.trackableItemName}</p>
                            </div>
                        </div>
                    )}

                    {order.createdAt && (
                        <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-sm text-gray-600">Created</span>
                                <p className="text-gray-900 text-sm">
                                    <RelativeTime timestamp={order.createdAt} />
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Auth Method</span>
                            <p className="text-gray-900">{getAuthMethodDisplay(order.authMethod)}</p>
                        </div>
                    </div>

                    <div className="flex items-start gap-2">
                        <RotateCcw className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-sm text-gray-600">Returned</span>
                            <p className="text-gray-900 text-sm">
                                {order.returnedAt ? (
                                    <RelativeTime timestamp={order.returnedAt} />
                                ) : (
                                    'Not returned'
                                )}
                            </p>
                        </div>
                    </div>

                    {order.isVirtualProduct && order.virtualConfirmationId && (
                        <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-sm text-gray-600">Confirmation ID</span>
                                <p className="text-gray-900 font-mono text-sm">{order.virtualConfirmationId}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Refund Status */}
                {order.refundStatus?.refunded && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-red-800 mb-2">
                            <RotateCcw className="w-4 h-4" />
                            <span className="font-medium text-sm">Refunded</span>
                        </div>
                        <div className="text-xs text-red-700 space-y-1">
                            <p>By: {order.refundStatus.refundAdmin}</p>
                            {order.refundStatus.refundedAt && (
                                <p>At: <RelativeTime timestamp={order.refundStatus.refundedAt} /></p>
                            )}
                            {order.refundStatus.refundNote && (
                                <p>Note: {order.refundStatus.refundNote}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Nayax Capture Actions */}
                {order.isNayaxOrder && (
                    <div className="mb-4">
                        <NayaxCaptureActions order={order} onActionComplete={fetchOrders} />
                    </div>
                )}

                {/* Action Button */}
                {isRefundable(order) && (
                    <button
                        onClick={() => handleRefundClick(order)}
                        disabled={isRefunding}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                        <RotateCcw className="w-4 h-4" />
                        {isRefunding && selectedOrder?.uuid === order.uuid ? 'Processing...' : 'Refund'}
                    </button>
                )}
            </div>
        </div>
    );

    const OrderListItem = ({ order }: { order: Order }) => (
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                {/* Status & Order Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {OrderStatus[order.status]}
                        </span>
                        {order.isVirtualProduct && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <Globe className="w-3 h-3" />
                                Virtual
                            </span>
                        )}
                        <span className="font-mono text-xs text-gray-500 truncate">{order.uuid}</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-9 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">Customer</p>
                            {order.customer ? (
                                <MobileNumberActions
                                    mobileNumber={order.customer.mobileNumber}
                                    customerUuid={order.customer.uuid}
                                    onSMSClick={() => setSmsTarget({ uuid: order.customer!.uuid, mobileNumber: order.customer!.mobileNumber })}
                                />
                            ) : (
                                <span className="text-gray-500">Unknown</span>
                            )}
                        </div>
                        <div>
                            <p className="text-gray-600">Product</p>
                            <div className="flex items-center gap-2">
                                <p className="font-medium">
                                    {order.isVirtualProduct ? order.virtualProductName || order.productName : order.productName}
                                </p>
                                {order.isVirtualProduct && order.virtualCategoryName && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                        {order.virtualCategoryName}
                                    </span>
                                )}
                                {isProductForSale(order.productUuid) && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        For Sale
                                    </span>
                                )}
                            </div>
                            {order.isVirtualProduct && order.virtualProductSku && (
                                <p className="text-xs text-gray-500">SKU: {order.virtualProductSku}</p>
                            )}
                        </div>
                        <div>
                            <p className="text-gray-600">Price</p>
                            <p className="font-bold"><CurrencyFormatter priceInCents={order.priceInCents} /></p>
                        </div>
                        <div>
                            <p className="text-gray-600">Payment</p>
                            {order.paymentInformation ? (
                                <>
                                    <p className="font-medium">
                                        {order.paymentInformation.cardBrand} •••• {order.paymentInformation.last4}
                                    </p>
                                    {order.isNayaxOrder && (order.paymentInformation.rrn || order.paymentInformation.paymentDeviceSessionId) && (
                                        <p className="text-xs text-gray-500">
                                            {order.paymentInformation.rrn && <>RRN: {order.paymentInformation.rrn}</>}
                                            {order.paymentInformation.rrn && order.paymentInformation.paymentDeviceSessionId && <> · </>}
                                            {order.paymentInformation.paymentDeviceSessionId && <>Session: {order.paymentInformation.paymentDeviceSessionId}</>}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-gray-500">N/A</p>
                            )}
                        </div>
                        <div>
                            <p className="text-gray-600">Machine/Location</p>
                            <p className="font-medium">{order.machineName}</p>
                            <p className="text-xs text-gray-500">Door: {order.doorIdDisplay}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Rental Start</p>
                            <p className="text-gray-900">
                                {order.createdAt ? <RelativeTime timestamp={order.createdAt} /> : 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-600">Auth Method</p>
                            <p className="font-medium">{getAuthMethodDisplay(order.authMethod)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Trackable Item</p>
                            <p className="font-medium">{order.trackableItemName || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Returned</p>
                            <p className="text-gray-900">
                                {order.returnedAt ? <RelativeTime timestamp={order.returnedAt} /> : 'Not returned'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Nayax Capture Actions (list view) */}
                {order.isNayaxOrder && (
                    <div className="flex-shrink-0">
                        <NayaxCaptureActions order={order} onActionComplete={fetchOrders} />
                    </div>
                )}

                {/* Actions */}
                <div className="flex-shrink-0">
                    {isRefundable(order) && (
                        <button
                            onClick={() => handleRefundClick(order)}
                            disabled={isRefunding}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Refund"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                    )}
                    {order.refundStatus?.refunded && (
                        <div className="p-2 text-red-600">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const PaginationControls = () => {
        const pageNumbers = [];
        const delta = 2;
        
        for (let i = 1; i <= totalPages; i++) {
            if (i <= delta || i > totalPages - delta || (i >= page - delta && i <= page + delta)) {
                pageNumbers.push(i);
            } else if (pageNumbers[pageNumbers.length - 1] !== '...') {
                pageNumbers.push('...');
            }
        }

        return (
            <div className="flex items-center justify-center gap-1 flex-wrap">
                <button
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                >
                    <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                
                {pageNumbers.map((number, index) =>
                    number === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-3 py-2">...</span>
                    ) : (
                        <button
                            key={`page-${number}`}
                            onClick={() => handlePageChange(number as number)}
                            className={`px-3 py-2 rounded-lg transition-colors ${
                                number === page 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            {number}
                        </button>
                    )
                )}
                
                <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                >
                    <ChevronsRight className="w-4 h-4" />
                </button>
            </div>
        );
    };

    return (
        <div className="p-6 animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Leases</h1>
                        <p className="text-gray-600">Manage your vending machine lease transactions</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'grid' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'list' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Filters */}
            <OrderFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                isLoading={isLoading}
            />

            {/* Date Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Date Range</h3>
                    <p className="text-sm text-gray-600">Filter orders by creation date</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <DatePicker
                                className="input-modern pl-10"
                                placeholderText="Start Date"
                                selected={startDate}
                                onChange={handleStartDateChange}
                                dateFormat="yyyy-MM-dd"
                                isClearable
                                maxDate={new Date()}
                            />
                        </div>
                        <span className="text-gray-400">to</span>
                        <div className="relative">
                            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <DatePicker
                                className="input-modern pl-10"
                                placeholderText="End Date"
                                selected={endDate}
                                onChange={handleEndDateChange}
                                dateFormat="yyyy-MM-dd"
                                isClearable
                                maxDate={new Date()}
                            />
                        </div>
                    </div>
                    <button
                        className="btn-primary flex items-center gap-2"
                        onClick={downloadCsv}
                    >
                        <Download className="w-4 h-4" />
                        Download CSV
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                        <p className="text-3xl font-bold text-gray-900">{totalOrders.toLocaleString()}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, totalOrders)} of {totalOrders}
                        </p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                        <FileText className="w-8 h-8 text-white" />
                    </div>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading orders...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="text-center py-12">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Orders</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            ) : orders.length === 0 ? (
                <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h2>
                    <p className="text-gray-600">
                        {startDate || endDate || filters.machineUuids.length > 0 || filters.last4Digits || filters.productUuids.length > 0 
                            ? 'No orders match your filter criteria.' 
                            : 'No orders in the system yet.'}
                    </p>
                </div>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {orders.map((order) => (
                                <OrderCard key={order.uuid} order={order} />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3 mb-8">
                            {orders.map((order) => (
                                <OrderListItem key={order.uuid} order={order} />
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                            <PaginationControls />
                        </div>
                    )}
                </>
            )}

            <SendSMSModal
                isOpen={smsTarget !== null}
                onClose={() => setSmsTarget(null)}
                customerUuid={smsTarget?.uuid ?? ''}
                mobileNumber={smsTarget?.mobileNumber ?? ''}
            />

            {/* Refund Modal */}
            <Modal show={showModal} onHide={handleRefundModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Refund</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group as={Row} controlId="formOrderUuid" className="mb-3">
                        <Form.Label column sm={4}>Order UUID</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} value={selectedOrder?.uuid || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formCustomerMobile" className="mb-3">
                        <Form.Label column sm={4}>Customer</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} value={selectedOrder?.customer?.mobileNumber || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formRefundPrice" className="mb-3">
                        <Form.Label column sm={4}>Refund Price In Cents</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} value={selectedOrder?.priceInCents || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formRefundNote" className="mb-3">
                        <Form.Label column sm={4}>Refund Note</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" placeholder="Please enter the refund note" onChange={handleNoteChange} />
                        </Col>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleRefundModalClose}>Cancel</Button>
                    <Button variant="primary" onClick={confirmRefund}>Confirm</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Leases;